import * as React from 'react';
import { createContext, useContext } from 'react';
import { getHasModeFeature } from './mode-features';
import { MODE } from '../../../constants';

type ModeContextProviderProps = {
  children: React.ReactNode;
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2503
  mode: MODE.FILE_REQUEST | MODE.FORM;
};

type ModeContextValue = {
  // hasModeFeature: checks if the string argument exists in the current mode feature set and returns true/false
  hasModeFeature: (arg1: string) => boolean;
  // mode: the current mode set by current context
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2503
  mode: MODE.FILE_REQUEST | MODE.FORM;
};

const defaultValue: ModeContextValue = {
  mode: MODE.FILE_REQUEST,
  hasModeFeature: getHasModeFeature(MODE.FILE_REQUEST),
};

const ModeContext = createContext<ModeContextValue>(defaultValue);

// "Mode" here specifically refers to if we are currently in the File Request product mode
// or the Forms product mode
const ModeContextProvider = ({ children, mode }: ModeContextProviderProps) => {
  return (
    <ModeContext.Provider
      value={{ mode, hasModeFeature: getHasModeFeature(mode) }}
    >
      {children}
    </ModeContext.Provider>
  );
};

// Higher Order Component to pass mode and hasModeFeature as props to another component
// Only use this if you need mode or hasModeFeature in a class component
const withModeContext =
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  (Component: React.ComponentType<any>) => (props: any) => (
    <ModeContext.Consumer>
      {({ mode, hasModeFeature }) => (
        <Component {...props} hasModeFeature={hasModeFeature} mode={mode} />
      )}
    </ModeContext.Consumer>
  );

// Hook to grab ModeContext value in any functional component
// const { mode, hasModeFeature } = useModeContext();
const useModeContext = () => useContext(ModeContext);

// Hook to directly check hasModeFeature within any functional component
// const isFeatureEnabled = useModeFeature(feature)
const useModeFeature = (feature: string): boolean => {
  const { hasModeFeature } = useModeContext();
  return hasModeFeature(feature);
};

export {
  ModeContextProvider,
  useModeContext,
  withModeContext,
  ModeContext,
  useModeFeature,
};
